/**
 * ThemeContext — Central theme provider for the design system.
 *
 * Supports two theme formats:
 * 1. Built-in (string) — e.g. 'brand', 'blue', 'red'. Looked up from THEMES map
 *    and persisted to localStorage so the choice survives page refreshes.
 * 2. Custom/backend (object) — e.g. { id: 'acme', semanticColors: { ... } }.
 *    Applied at runtime; NOT persisted (re-apply from API on next load).
 *
 * How it works:
 *   ThemeProvider → watches `theme` state → on change, calls applyThemeToDom()
 *   which sets CSS variables (--color-*) on <html>, making the whole page re-theme
 *   without a reload.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { THEMES, THEME_NAMES, applyThemeToDom } from '../tokens/themes';

const ThemeContext = createContext(null);

/** localStorage key for persisting the active built-in theme name */
const THEME_STORAGE_KEY = 'kb-theme';

/**
 * Reads initial theme from localStorage.
 * Falls back to 'brand' if nothing is stored or the stored name is invalid.
 */
function getInitialTheme() {
  if (typeof window === 'undefined') return 'brand'; // SSR safety
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return THEME_NAMES.includes(stored) ? stored : 'brand';
}

/** Returns true if `value` is a recognized built-in theme name */
function isBuiltInTheme(value) {
  return typeof value === 'string' && THEME_NAMES.includes(value);
}

/**
 * ThemeProvider — Wrap your app with this to enable theming.
 *
 * @param {React.ReactNode} children
 * @param {string}  [defaultTheme]        - Initial built-in theme (overrides localStorage)
 * @param {Object}  [initialCustomTheme]  - Backend theme object to apply on first render
 *
 * Priority: initialCustomTheme > defaultTheme > localStorage > 'brand'
 */
export function ThemeProvider({ children, defaultTheme, initialCustomTheme }) {
  const [theme, setThemeState] = useState(() => initialCustomTheme ?? defaultTheme ?? getInitialTheme());

  // Apply CSS variables whenever the theme changes
  useEffect(() => {
    if (typeof document === 'undefined') return; // SSR guard
    const root = document.documentElement;

    if (isBuiltInTheme(theme)) {
      // Built-in: apply from the THEMES map and persist the name
      applyThemeToDom(THEMES[theme].semanticColors, root);
      root.setAttribute('data-theme', theme);
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
      } catch (_) { /* localStorage may be unavailable (incognito, storage full) */ }
    } else if (theme && typeof theme === 'object' && theme.semanticColors) {
      // Custom/backend: apply the raw semanticColors and clear localStorage
      // (custom themes are transient — the app should re-fetch from API on reload)
      applyThemeToDom(theme.semanticColors, root);
      root.setAttribute('data-theme', theme.id ?? 'custom');
      try {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
      } catch (_) {}
    }
  }, [theme]);

  /**
   * setTheme — Switch the active theme.
   *
   * Accepts either:
   *   - A built-in theme name string: setTheme('blue')
   *   - A custom theme object:        setTheme({ id: 'acme', semanticColors: { ... } })
   *
   * Invalid values are silently ignored to prevent broken states.
   */
  const setTheme = useCallback((next) => {
    if (typeof next === 'string' && THEME_NAMES.includes(next)) {
      setThemeState(next);
      return;
    }
    if (next && typeof next === 'object' && next.semanticColors) {
      setThemeState(next);
      return;
    }
    // Invalid input — do nothing (could add console.warn here in dev)
  }, []);

  // Memoize context value to avoid re-rendering all consumers on every provider render
  const value = useMemo(() => ({
    theme,
    setTheme,
    themes: THEMES,
    themeNames: THEME_NAMES,
    isBuiltInTheme: isBuiltInTheme(theme),
  }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme — Access the current theme and theme controls.
 *
 * Returns: { theme, setTheme, themes, themeNames, isBuiltInTheme }
 *
 * Must be called inside a <ThemeProvider>.
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
